import { useMemo } from 'react'
import { NetworkGraph as SharedNetworkGraph } from '@/shared/ui/NetworkGraph'
import type {
  NetworkGraphData,
  GraphNode,
  GraphLink,
} from '@/features/metrics/model/contracts'
import type { TopicNetworkResponse } from '../model/contracts'
import type { NetworkLayout } from '../state/useNetworkLayout'
import { useContainerDimensions } from '../state/useContainerDimensions'
import { ForceGraph3DWrapper } from './ForceGraph3DWrapper'

interface NetworkGraphProps {
  response: TopicNetworkResponse
  highlightKeywords?: string[]
  onNodeClick?: (node: GraphNode) => void
  layout?: NetworkLayout
}

export function NetworkGraph({
  response,
  highlightKeywords,
  onNodeClick,
  layout = '2d',
}: NetworkGraphProps) {
  const graphData = useMemo<NetworkGraphData>(() => {
    const nodes: GraphNode[] = response.nodes.map((n) => ({
      id: n.id,
      label: n.label,
      type: n.type as GraphNode['type'],
      group: n.group,
      influence: n.influence,
      color: n.color,
    }))
    const links: GraphLink[] = response.links.map((l) => ({
      source: l.source,
      target: l.target,
      weight: l.weight,
      sentiment: l.sentiment as GraphLink['sentiment'],
    }))
    return { nodes, links, groups: response.groups }
  }, [response])

  const { containerRef, dimensions } = useContainerDimensions('full')

  if (layout === '3d') {
    return (
      <div
        ref={containerRef}
        className="relative min-h-[320px] overflow-hidden rounded-xl border border-primary/10 bg-muted shadow-[0_0_24px_-6px_var(--shadow-brand-highlight)] lg:min-h-0"
        style={{ height: dimensions.height }}
      >
        <ForceGraph3DWrapper
          nodes={graphData.nodes}
          links={graphData.links}
          width={dimensions.width}
          height={dimensions.height}
          onNodeClick={onNodeClick}
        />
      </div>
    )
  }

  return (
    <SharedNetworkGraph
      data={graphData}
      heightMode="full"
      highlightKeywords={highlightKeywords}
      onNodeClick={onNodeClick}
    />
  )
}
